import { useEffect, useRef, useState } from 'react';

import { ObservableQuery } from '@apollo/client';
import { debounce, isEmpty } from 'lodash';

import { useService } from '../../store/useService';
import { SUGGEST_MODELS } from './MPNsToReceiveService';

type SetVariables = (vars: SuggestVariables) => void;
type ThrottledSetVariables = SetVariables & Cancelable;

interface Cancelable {
  cancel(): void;
}

interface SuggestVariables {
  expectedMPNPrefix: string;
  referenceId: string;
}

interface UseAutosuggestOptions extends SuggestVariables {
  delay?: number;
  disableSearch: boolean;
}

export const useMpnSearch = ({ expectedMPNPrefix = '', referenceId = '', disableSearch }: UseAutosuggestOptions) => {
  const delay = 100;
  const [suggestions, setSuggestions] = useState<any>({});

  const queryObserver = useRef<ObservableQuery<any, any>>();
  const querySubscription = useRef<any>();
  const throttledInvoke = useRef<ThrottledSetVariables>();
  const { client } = useService();
  const queryClient = client();
  useEffect(
    () => {
      const subscribeFn = ({ data, error, loading }: any) => {
        if (queryObserver.current?.variables?.expectedMPNPrefix?.trim().length < 3) {
          setSuggestions({ values: [], error: null, loading: false });
          return;
        }

        if (
          isEmpty(data?.getMpnAutoCorrection?.validMPN) &&
          isEmpty(queryObserver.current?.variables?.expectedMPNPrefix)
        ) {
          setSuggestions({ values: [], error, loading: false });
          return;
        }

        const values = data?.getMpnAutoCorrection ? [data.getMpnAutoCorrection] : [];
        setSuggestions({ values, error, loading });
      };
      queryObserver.current = queryClient.watchQuery({
        query: SUGGEST_MODELS,
        fetchPolicy: 'no-cache',
        errorPolicy: 'ignore',
        notifyOnNetworkStatusChange: true,
        variables: { expectedMPNPrefix, referenceId },
      });

      if (queryObserver.current && !disableSearch) {
        querySubscription.current = queryObserver.current.subscribe(subscribeFn);
      }
      throttledInvoke.current = debounce(
        (variables: any) => {
          // if the query has been closed, reopen it and resubscribe to the new query
          if (queryObserver.current && querySubscription.current?._state === 'closed') {
            querySubscription.current = queryObserver.current.subscribe(subscribeFn);
          }

          if (queryObserver.current) {
            queryObserver.current.setVariables(variables);
          }
        },
        delay,
        { leading: false, trailing: true },
      );

      return () => {
        if (querySubscription.current) {
          querySubscription.current.unsubscribe();
        }
        if (throttledInvoke.current) {
          throttledInvoke.current.cancel();
        }
      };
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [disableSearch],
  );

  const cancelSubscription = () => {
    if (querySubscription.current) {
      querySubscription.current.unsubscribe();
    }
    setSuggestions({ values: [], error: null, loading: false });
  };

  const closePopover = () => {
    setSuggestions({ values: [], error: null, loading: false });
  };

  useEffect(() => {
    /* istanbul ignore next */
    if (!throttledInvoke.current || disableSearch) {
      return;
    }
    if (expectedMPNPrefix?.trim().length === 0) {
      closePopover();
    }

    if (expectedMPNPrefix?.trim().length <= 2) {
      return;
    }
    throttledInvoke.current({ referenceId, expectedMPNPrefix });
  }, [expectedMPNPrefix, setSuggestions, referenceId, disableSearch]);

  return {
    suggestions,
    closePopover,
    cancelSubscription,
  };
};

export default useMpnSearch;
